/**
 * Validation utilities for input sanitization and validation
 * Prevents security issues and data integrity problems
 */

import { VALIDATION_CONSTANTS } from "./constants";

/**
 * Validates email format
 * @param email - Email address to validate
 * @returns true if valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
	if (!email || typeof email !== "string") {
		return false;
	}

	const trimmed = email.trim().toLowerCase();
	if (trimmed.length > VALIDATION_CONSTANTS.EMAIL_MAX_LENGTH) {
		return false;
	}

	// RFC 5322 compliant email regex (simplified)
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(trimmed);
}

/**
 * Validates and sanitizes email input
 * @param email - Raw email input
 * @returns Sanitized email or null if invalid
 */
export function sanitizeEmail(email: string): string | null {
	if (!email || typeof email !== "string") {
		return null;
	}

	const trimmed = email.trim().toLowerCase();
	if (!isValidEmail(trimmed)) {
		return null;
	}

	return trimmed;
}

/**
 * Validates group name
 * @param name - Group name to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export function validateGroupName(name: string): {
	isValid: boolean;
	error?: string;
} {
	if (!name || typeof name !== "string") {
		return { isValid: false, error: "Group name is required" };
	}

	const trimmed = name.trim();
	if (trimmed.length === 0) {
		return { isValid: false, error: "Group name cannot be empty" };
	}

	if (trimmed.length > VALIDATION_CONSTANTS.GROUP_NAME_MAX_LENGTH) {
		return {
			isValid: false,
			error: `Group name must be ${VALIDATION_CONSTANTS.GROUP_NAME_MAX_LENGTH} characters or less`,
		};
	}

	return { isValid: true };
}

/**
 * Validates group description
 * @param description - Group description to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export function validateGroupDescription(description: string): {
	isValid: boolean;
	error?: string;
} {
	if (!description) {
		return { isValid: true }; // Description is optional
	}

	if (typeof description !== "string") {
		return { isValid: false, error: "Description must be text" };
	}

	if (description.length > VALIDATION_CONSTANTS.GROUP_DESCRIPTION_MAX_LENGTH) {
		return {
			isValid: false,
			error: `Description must be ${VALIDATION_CONSTANTS.GROUP_DESCRIPTION_MAX_LENGTH} characters or less`,
		};
	}

	return { isValid: true };
}

/**
 * Sanitizes text input by trimming and removing dangerous characters
 * @param input - Raw text input
 * @param maxLength - Maximum allowed length
 * @returns Sanitized text or null if invalid
 */
export function sanitizeText(input: string, maxLength: number): string | null {
	if (!input || typeof input !== "string") {
		return null;
	}

	const trimmed = input.trim();
	if (trimmed.length === 0) {
		return null;
	}

	if (trimmed.length > maxLength) {
		return null;
	}

	// Remove potentially dangerous characters (basic XSS prevention)
	const sanitized = trimmed.replace(/[<>]/g, "");
	return sanitized;
}

/**
 * Validates UUID format
 * @param uuid - UUID string to validate
 * @returns true if valid UUID format, false otherwise
 */
export function isValidUUID(uuid: string): boolean {
	if (!uuid || typeof uuid !== "string") {
		return false;
	}

	const uuidRegex =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
	return uuidRegex.test(uuid);
}
